import { AnimatedOutlet } from "@/app/components/animated-outlet";
import { useRealtimeActies } from "@/entities/actie";
import { useRealtimeBerichten } from "@/entities/bericht";
import { useRealtimeBetrokkenStations } from "@/entities/betrokken-stations";
import { useRealtimeBetrokkenTreinen } from "@/entities/betrokken-treinen";
import { useRealtimeContactpersonen } from "@/entities/contactpersoon";
import { useRealtimeDossierDetails } from "@/entities/dossier-details";
import { useRealtimeDossierOverzicht } from "@/entities/dossier-overzicht";
import { useRealtimeDossierSettings } from "@/entities/dossier-settings";
import { useRealtimeHerinneringen } from "@/entities/herinnering/hooks.ts";
import { useRealtimeLogboek } from "@/entities/logboek";
import { useRealtimeMedewerkersOpStation } from "@/entities/medewerkers-op-station";
import { useRealtimeOngewensteGebeurtenissen } from "@/entities/ongewenste-gebeurtenis";
import { useRealtimeOpstartplan } from "@/entities/opstartplan";
import { usePrefetchStations } from "@/entities/station";
import { useRealtimeSuggesties } from "@/entities/suggestie";
import { usePrefetchTreinseriesForToday } from "@/entities/treinserie";
import { useRealtimeTvv } from "@/entities/tvv";
import { useRealtimeLandelijkeFaciliteiten, useRealtimeVestigingen } from "@/entities/vestiging";
import { useRealtimeVsm } from "@/entities/vsm";
import { DossierOverzicht } from "@/features/dossier-balk";
import { Toaster } from "@/shared/components";
import { ErrorBoundary, ErrorDisplay } from "@/shared/components/error";

/**
 * Mounts every realtime subscription at HomePage level so the subscriptions
 * stay alive across dossier-id changes and any subtree remounts. Each hook
 * gates itself by role via REALTIME_TOPIC_ROLES — users without access to a
 * topic skip the subscription.
 */
function useRealtimeSubscriptions() {
    useRealtimeActies();
    useRealtimeBerichten();
    useRealtimeBetrokkenStations();
    useRealtimeBetrokkenTreinen();
    useRealtimeContactpersonen();
    useRealtimeDossierDetails();
    useRealtimeDossierOverzicht();
    useRealtimeDossierSettings();
    useRealtimeLogboek();
    useRealtimeMedewerkersOpStation();
    useRealtimeOngewensteGebeurtenissen();
    useRealtimeOpstartplan();
    useRealtimeSuggesties();
    useRealtimeTvv();
    useRealtimeVestigingen();
    useRealtimeLandelijkeFaciliteiten();
    useRealtimeVsm();
    useRealtimeHerinneringen();
}

export default function HomePage() {
    // prefetch treinseries for today
    // This ensures that when the user opens the vsm-dialog, we should already have the most recent treinseries for today
    usePrefetchTreinseriesForToday();
    useRealtimeSubscriptions();

    // Prefetch stations because we need this data in many places. One benefit is that station names are available immediately.
    usePrefetchStations();

    return (
        <ErrorBoundary
            fallback={(error, reset) => (
                <ErrorDisplay error={error} variant="section" title="Fout bij het laden van de homepagina" onRetry={reset} />
            )}
        >
            <div className="flex flex-1 flex-col">
                <DossierOverzicht />
                <div className="relative flex w-full flex-1">
                    <div className="bg-accent-gray-subtle absolute inset-0">
                        <div className="relative flex h-full w-full">
                            <AnimatedOutlet />
                        </div>
                    </div>
                </div>
                <Toaster />
            </div>
        </ErrorBoundary>
    );
}
